/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable max-len */

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin, Tooltip } from 'antd';
import { CloseOutlined, DownloadOutlined, FullscreenOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { defaultStyles, FileIcon } from 'react-file-icon';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import ENVIRONMENT from 'config/environment';
import DownloadFileButton from 'components/DownloadFileButton';
import DownloadFileCard from 'modules/fileViewer/components/DownloadFileCard';
import { UploadResponse } from 'types/upload-type';
import styles from './styles.module.less';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'tif', 'svg', 'bmp', 'tiff', 'ai', 'lsm'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'aac', 'flac', 'm4a'];
const IFRAME_EXTENSIONS = ['pdf', 'json'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const TEXT_EXTENSIONS = ['txt', 'csv', 'md', 'xml', 'html', 'css', 'js', 'ts'];

export type FileViewerFile = UploadResponse & { url?: string };

interface FileViewerModalProps {
  files: FileViewerFile[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

const FileViewerModal: FC<FileViewerModalProps> = ({ files, initialIndex = 0, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textLoading, setTextLoading] = useState(false);
  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeError, setOfficeError] = useState(false);

  const stripRef = useRef<HTMLDivElement>(null);
  const prevTextRef = useRef<string | null>(null);
  const officeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const file = files[currentIndex];
  const ext = file?.extension.toLowerCase() ?? '';
  const fileUrl = file ? (file.url || `${ENVIRONMENT.UPLOADS_URL}/${file.uuid}.${file.extension}`) : '';

  const goTo = (newIndex: number, dir: 'left' | 'right') => {
    if (newIndex === currentIndex || newIndex < 0 || newIndex >= files.length || isAnimating) return;
    if (TEXT_EXTENSIONS.includes(ext)) {
      prevTextRef.current = textContent;
    }
    setSlideDir(dir);
    setPrevIndex(currentIndex);
    setIsAnimating(true);
    setCurrentIndex(newIndex);
  };

  const handleAnimationEnd = () => {
    setPrevIndex(null);
    setIsAnimating(false);
  };

  const handleOfficeIframeLoad = useCallback(() => {
    setOfficeLoading(false);
    if (officeTimeoutRef.current) {
      clearTimeout(officeTimeoutRef.current);
      officeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const f = files[currentIndex];
    if (!f) return;
    const fExt = f.extension.toLowerCase();
    if (!OFFICE_EXTENSIONS.includes(fExt)) return;

    setOfficeLoading(true);
    setOfficeError(false);

    officeTimeoutRef.current = setTimeout(() => {
      setOfficeLoading(false);
      setOfficeError(true);
    }, 15000);

    return () => {
      if (officeTimeoutRef.current) {
        clearTimeout(officeTimeoutRef.current);
        officeTimeoutRef.current = null;
      }
    };
  }, [currentIndex, files]);

  useEffect(() => {
    if (!stripRef.current) return;
    const strip = stripRef.current;
    const activeThumb = strip.children[currentIndex] as HTMLElement | undefined;
    if (!activeThumb) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();
    const scrollTarget = activeThumb.offsetLeft - stripRect.width / 2 + thumbRect.width / 2;
    strip.scrollTo({ left: scrollTarget, behavior: prevIndex !== null ? 'smooth' : 'auto' });
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setPrevIndex(null);
      setIsAnimating(false);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1, 'left');
      if (e.key === 'ArrowRight') goTo(currentIndex + 1, 'right');
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, files.length, onClose, currentIndex, isAnimating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!file || !TEXT_EXTENSIONS.includes(ext)) return;
    setTextContent(null);
    setTextLoading(true);
    fetch(fileUrl)
      .then((r) => r.text())
      .then((t) => setTextContent(t))
      .catch(() => setTextContent(null))
      .finally(() => setTextLoading(false));
  }, [file?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!files.length) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await apiRequests.get(`${apiRoutes.UPLOADS}/${file.uuid}/download`);
      const link = document.createElement('a');
      link.href = response.data;
      link.download = file.name;
      link.click();
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setDownloading(false);
    }
  };

  const renderContentForIndex = (idx: number) => {
    const f = files[idx];
    if (!f) return null;
    const fExt = f.extension.toLowerCase();
    const fUrl = f.url || `${ENVIRONMENT.UPLOADS_URL}/${f.uuid}.${f.extension}`;

    if (IMAGE_EXTENSIONS.includes(fExt)) {
      return (
        <img
          src={fUrl}
          alt={f.name}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', userSelect: 'none' }}
        />
      );
    }

    if (VIDEO_EXTENSIONS.includes(fExt)) {
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video controls style={{ maxWidth: '100%', maxHeight: '100%' }}>
          <source src={fUrl} />
        </video>
      );
    }

    if (AUDIO_EXTENSIONS.includes(fExt)) {
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio controls style={{ width: '80%' }}>
          <source src={fUrl} />
        </audio>
      );
    }

    if (IFRAME_EXTENSIONS.includes(fExt)) {
      return (
        <iframe
          src={fUrl}
          title={f.name}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      );
    }

    if (OFFICE_EXTENSIONS.includes(fExt)) {
      if (idx === currentIndex && officeError) {
        return <DownloadFileCard downloading={downloading} onDownload={handleDownload} />;
      }

      return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          {idx === currentIndex && officeLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
              <Spin size="large" />
            </div>
          )}
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fUrl)}`}
            title={f.name}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            onLoad={idx === currentIndex ? handleOfficeIframeLoad : undefined}
          />
        </div>
      );
    }

    if (TEXT_EXTENSIONS.includes(fExt)) {
      const content = idx === currentIndex ? textContent : prevTextRef.current;
      if (idx === currentIndex && textLoading) return <Spin />;
      if (content !== null) {
        return (
          <pre
            style={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              background: '#111',
              color: '#e0e0e0',
              padding: 20,
              borderRadius: 0,
              fontSize: 13,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              margin: 0,
            }}
          >
            {content}
          </pre>
        );
      }
    }

    return <DownloadFileCard downloading={downloading} onDownload={handleDownload} />;
  };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      title={null}
      closable={false}
      width="100vw"
      style={{ top: 0, padding: 0 }}
      maskStyle={{ background: 'rgba(0,0,0,0.8)' }}
      wrapClassName={styles.galleryModal}
      destroyOnClose
    >
      <div className={styles.fullscreenWrapper}>

        <div className={styles.toolbar}>
          {files.length > 1 && (
            <span className={styles.counter}>{currentIndex + 1} / {files.length}</span>
          )}

          <DownloadFileButton
            hideTooltip
            uuid={file.uuid}
            name={file.name}
            icon={<DownloadOutlined />}
            className={styles.headerButton}
          />

          <Tooltip title="Open in full page">
            <Button
              type="text"
              size="small"
              icon={<FullscreenOutlined />}
              className={styles.headerButton}
              onClick={() => window.open(`/file-viewer/${file.uuid}`, '_blank')}
            />
          </Tooltip>

          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            className={styles.headerButton}
            onClick={onClose}
          />
        </div>

        <button
          type="button"
          className={styles.navButton}
          disabled={currentIndex === 0 || files.length <= 1}
          onClick={() => goTo(currentIndex - 1, 'left')}
          style={{ visibility: files.length <= 1 ? 'hidden' : undefined }}
        >
          <LeftOutlined />
        </button>

        <div className={styles.centerPanel}>

          <div className={styles.filenameBar}>
            <Tooltip title={file.name}>
              <span className={styles.filename}>{file.name}</span>
            </Tooltip>
          </div>

          <div className={styles.contentArea}>
            {prevIndex !== null && (
              <div
                key={`exit-${prevIndex}`}
                className={`${styles.slideWrapper} ${slideDir === 'right' ? styles.exitToLeft : styles.exitToRight}`}
              >
                {renderContentForIndex(prevIndex)}
              </div>
            )}

            <div
              key={`enter-${currentIndex}`}
              className={`${styles.slideWrapper} ${prevIndex !== null ? (slideDir === 'right' ? styles.enterFromRight : styles.enterFromLeft) : ''}`}
              onAnimationEnd={handleAnimationEnd}
            >
              {renderContentForIndex(currentIndex)}
            </div>
          </div>

          {files.length > 1 && (
            <div className={styles.thumbnailStrip} ref={stripRef}>
              {files.map((f, i) => {
                const fExt = f.extension.toLowerCase();
                const fUrl = f.url || `${ENVIRONMENT.UPLOADS_URL}/${f.uuid}.${f.extension}`;
                const fCustomStyles = fExt in defaultStyles ? defaultStyles[fExt as keyof typeof defaultStyles] : {};
                const isActive = i === currentIndex;

                return (
                  <div
                    key={f.uuid}
                    className={`${styles.thumbnail} ${isActive ? styles.thumbnailActive : ''}`}
                    onClick={() => goTo(i, i > currentIndex ? 'right' : 'left')}
                  >
                    {IMAGE_EXTENSIONS.includes(fExt) ? (
                      <img src={fUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : VIDEO_EXTENSIONS.includes(fExt) ? (
                      <video
                        src={`${fUrl}#t=0.1`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <div style={{ padding: 8, width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                        <FileIcon extension={f.extension.toUpperCase()} {...fCustomStyles} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className={styles.navButton}
          disabled={currentIndex === files.length - 1 || files.length <= 1}
          onClick={() => goTo(currentIndex + 1, 'right')}
          style={{ visibility: files.length <= 1 ? 'hidden' : undefined }}
        >
          <RightOutlined />
        </button>

      </div>
    </Modal>
  );
};

export default FileViewerModal;
